import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom';
import {PlusIcon, TagIcon} from "@heroicons/react/outline";
import {Controller, useForm} from "react-hook-form";
import InputFormattedNumber from "../form/input-formatted-number";
import {ImportButton} from "../../layout/page-header";
import {useTranslation} from "react-i18next";
import {TrashIcon} from "@heroicons/react/outline/esm";

export default function DiscountEditor({
    discounts,
    element,
    onAddDiscount,
    onUpdateDiscount,
    label = false,
    boxClass = 'ml-3',
    btnClass = '',
    fixedPosition = true,
    hover = 'bg-green-600 hover:bg-green-700',
    onOpenChange,
    showDiscountIndicator = false,
}) {

    const {control, watch,  setValue} = useForm();
    const { t } = useTranslation();

    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const rafRef = useRef(null);
    const [panelPosition, setPanelPosition] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isPortalReady, setPortalReady] = useState(false);

    useEffect(() => {
        setPortalReady(true);
    }, []);

    const handleOpenChange = useCallback((nextOpen) => {
        setIsOpen(nextOpen);
        if (typeof onOpenChange === "function") {
            onOpenChange(nextOpen);
        }
        if (!nextOpen) {
            setPanelPosition(null);
        }
    }, [onOpenChange]);

    const updatePanelPosition = useCallback(() => {
        if (!fixedPosition || !buttonRef.current || typeof window === "undefined") {
            return;
        }

        // Keep the discount editor panel anchored to the trigger while staying within the viewport bounds.
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const horizontalMargin = 16;
        const preferredWidth = 200;
        const maxAvailableWidth = Math.max(viewportWidth - (horizontalMargin * 2), 240);
        const width = Math.min(Math.max(preferredWidth, rect.width + 32), maxAvailableWidth);
        const centeredLeft = rect.left + (rect.width / 2) - (width / 2);
        const left = Math.min(
            Math.max(centeredLeft, horizontalMargin),
            Math.max(horizontalMargin, viewportWidth - width - horizontalMargin),
        );
        const top = rect.top;

        setPanelPosition({
            width: `${width}px`,
            left: `${left}px`,
            top: `${top}px`,
        });
    }, [fixedPosition]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const scheduleUpdate = () => {
            if (rafRef.current !== null) {
                window.cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = window.requestAnimationFrame(() => {
                rafRef.current = null;
                updatePanelPosition();
            });
        };

        updatePanelPosition();

        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', scheduleUpdate, true);

        const handleClickOutside = (event) => {
            if (buttonRef.current && buttonRef.current.contains(event.target)) {
                return;
            }
            if (panelRef.current && panelRef.current.contains(event.target)) {
                return;
            }
            handleOpenChange(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleOpenChange(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            if (rafRef.current !== null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', scheduleUpdate, true);
            document.removeEventListener('mousedown', handleClickOutside, true);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleOpenChange, isOpen, updatePanelPosition]);

    useEffect(() => {
        if (isOpen) {
            updatePanelPosition();
        }
    }, [discounts.length, isOpen, updatePanelPosition]);

    const lastDiscount = discounts?.[discounts.length - 1];
    const lastDiscountPrice = lastDiscount?.price;
    const isAddDisabled = discounts.length > 0 && (
        typeof lastDiscountPrice !== "number"
        || Number.isNaN(lastDiscountPrice)
    );

    useEffect(() => {
        // eslint-disable-next-line
        (discounts ?? []).map((item, index) => {
            const reference = item?.id ?? index;
            const numeric = item?.price ?? item;
            const parsed = numeric === null || numeric === undefined || numeric === ""
                ? undefined
                : Number(numeric);
            setValue("discount-" + reference, Number.isNaN(parsed) ? undefined : parsed);
        })

        // eslint-disable-next-line
    }, [discounts, setValue])
    const handleToggle = useCallback(() => {
        if (isOpen) {
            handleOpenChange(false);
            return;
        }
        handleOpenChange(true);
        if (typeof window !== 'undefined') {
            window.requestAnimationFrame(() => {
                updatePanelPosition();
            });
        }
    }, [handleOpenChange, isOpen, updatePanelPosition]);

    const renderPanel = () => {
        if (!isOpen || !isPortalReady) {
            return null;
        }

        if (fixedPosition && !panelPosition) {
            return null;
        }

        const style = fixedPosition
            ? {
                position: 'fixed',
                left: panelPosition.left,
                top: panelPosition.top,
                width: panelPosition.width,
                transform: `translateY(calc(-100% - 10px))`,
                maxWidth: 'min(200px, calc(100vw - 10px))',
                minWidth: 'min(200px, calc(100vw - 10px))',
                zIndex: 9999,
            }
            : {
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '12px',
                zIndex: 50,
            };

        const panel = (
            <div
                ref={panelRef}
                className="px-4 sm:px-0"
                style={style}
            >
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white">
                    <div className="relative bg-white p-2">
                        {discounts.length > 0 && (
                            <label className="block text-center text-sm font-medium mb-1 text-gray-700">
                                {t("app.quotations.discounts")}
                            </label>
                        )}
                        <div className="justify-items-center items-center text-center max-h-[150px] overflow-y-auto p-1">
                            {discounts.map((data, index) => (
                                <Fragment key={data.id}>
                                    <div className="w-full mb-2">
                                        <div className="flex items-center">
                                            <Controller
                                                name={"discount-" + data.id}
                                                rules={{required: true}}
                                                control={control}
                                                render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                                    <InputFormattedNumber
                                                        isFullW
                                                        label={t("app.quotations.discount")}
                                                        isShowLabel={false}
                                                        name={name}
                                                        value={value}
                                                        decimalScale={4}
                                                        onChange={(n) => {
                                                            onChange(n);
                                                            onUpdateDiscount(element.id, n, index);
                                                        }}
                                                        prefix={''}
                                                        suffix={' %'}
                                                        errors={error}
                                                    />
                                                )}
                                            />
                                            <button
                                                onClick={() => {
                                                    setValue("discount-" + data.id, undefined);
                                                    onUpdateDiscount(element.id, false, index);
                                                }}
                                                disabled={
                                                    (() => {
                                                        const currentValue = watch("discount-" + data.id);
                                                        return currentValue === undefined
                                                            || currentValue === null
                                                            || currentValue === "";
                                                    })()
                                                }
                                                className="ml-2 btn bg-red-500 hover:bg-red-700 disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                            >
                                                <TrashIcon
                                                    className="h-4 w-4 text-white"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    {(index + 1) !== discounts.length && (
                                        <b className="mx-4">+</b>
                                    )}
                                </Fragment>
                            ))}
                            <ImportButton
                                disabled={isAddDisabled}
                                extraClass="w-full rounded-sm h-8 w-full md:w-full"
                                onClick={(event) => {
                                    event.preventDefault();
                                    onAddDiscount(element.id);
                                }}
                                icon={(
                                    <PlusIcon
                                        className="w-4 h-4 fill-current flex-shrink-0"
                                        aria-hidden="true"
                                    />
                                )}
                                text={t("app.quotations.add_discount")}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );

        if (!fixedPosition) {
            return panel;
        }

        return createPortal(panel, document.body);
    };

    const indicatorDiscounts = useMemo(() => (
        (discounts ?? []).reduce((list, discount, index) => {
            const price = discount?.price;
            if (typeof price === "number" && !Number.isNaN(price)) {
                list.push({
                    id: discount?.id ?? index,
                    label: `${price.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 4})}%`,
                });
            }
            return list;
        }, [])
    ), [discounts]);

    const hasIndicatorDiscounts = showDiscountIndicator && indicatorDiscounts.length > 0;

    return (
        <div className={`relative ${boxClass}`}>
            <button
                ref={buttonRef}
                type="button"
                onClick={handleToggle}
                className={
                    "btn  disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed shadow-none   w-5 "
                    + hover
                    + "  "
                    + btnClass
                    + (showDiscountIndicator ? " relative" : "")
                }
            >
                <TagIcon
                    className="w-4 h-4 flex-shrink-0"
                    aria-hidden="true"
                />
                {hasIndicatorDiscounts && (
                    <span
                        className="absolute inline-flex h-3.5 w-3.5"
                        style={{top: "-0.5rem", right: "-0.6rem"}}
                    >
                        <span className="h-full w-full rounded-full bg-red-500 border border-white inline-flex" />
                    </span>
                )}
                {label && (
                    <div className="ml-3">{label}</div>
                )}
            </button>
            {renderPanel()}
        </div>
    );
}
